import { hasValidPublicUserIdentity, publicUserId } from "./public-user-identity.mjs";

const SANITIZED_KEYS = new Set(["ok", "verified", "purpose", "identityChanged"]);
const CLEARED_KEYS = new Set(["ok", "verified", "user", "contactVerificationDeviceCleared"]);
const CLEARED_KEYS_WITH_BOOTSTRAP = new Set([...CLEARED_KEYS, "localDataBootstrapRequired"]);
const IDENTITY_CHANGED_KEYS = new Set(["ok", "verified", "purpose", "identityChanged", "user"]);

function isRecord(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasOwn(record, key) {
  return Object.prototype.hasOwnProperty.call(record, key);
}

function hasOnlyKeys(record, allowed) {
  const keys = Object.keys(record);
  return keys.length === allowed.size && keys.every((key) => allowed.has(key));
}

function hasOnlyClearedKeys(record) {
  if (!hasOwn(record, "localDataBootstrapRequired")) {
    return hasOnlyKeys(record, CLEARED_KEYS);
  }
  return (
    record.localDataBootstrapRequired === true && hasOnlyKeys(record, CLEARED_KEYS_WITH_BOOTSTRAP)
  );
}

function invalidCompletion() {
  const error = new Error("invalid_auth_session_response");
  error.code = "invalid_auth_session_response";
  return error;
}

/**
 * Parses ordinary verification-link completions that intentionally carry no
 * tokens. Signed-out secondary-phone verification is sanitized, identity
 * change identifies the exact changed phone without granting a session, and
 * an already-signed-in verification is followed by authoritative /me.
 */
export function readOrdinaryVerificationLinkCompletion(value) {
  if (!isRecord(value)) return null;
  const hasSignal =
    hasOwn(value, "verified") || hasOwn(value, "purpose") || hasOwn(value, "identityChanged");
  if (!hasSignal) return null;

  const signedOut =
    value.ok === true &&
    value.verified === true &&
    value.purpose === "verification" &&
    value.identityChanged === false &&
    hasOnlyKeys(value, SANITIZED_KEYS);
  if (signedOut) return { kind: "signed_out" };

  const identityChanged =
    value.ok === true &&
    value.verified === true &&
    value.purpose === "identity_change" &&
    value.identityChanged === true &&
    hasValidPublicUserIdentity(value.user) &&
    value.user?.phoneVerified === true &&
    String(value.user?.phoneE164 || value.user?.phone || "").startsWith("+91") &&
    hasOnlyKeys(value, IDENTITY_CHANGED_KEYS);
  if (identityChanged) return { kind: "identity_changed", user: value.user };

  const cleared =
    value.ok === true &&
    value.verified === true &&
    value.contactVerificationDeviceCleared === true &&
    hasValidPublicUserIdentity(value.user) &&
    hasOnlyClearedKeys(value) &&
    !hasOwn(value, "error") &&
    !hasOwn(value, "code");
  if (cleared) return { kind: "cleared", user: value.user };

  throw invalidCompletion();
}

/**
 * Routes a verification-link response using the signed purpose before any
 * session material can be applied. Identity-change links are deliberately
 * tokenless and may only return the exact identity-changed completion.
 */
export function resolveVerificationLinkCompletionForPurpose(
  linkPurpose,
  value,
  applyAuthenticatedSession,
) {
  if (linkPurpose === "contact_sign_in") throw invalidCompletion();

  if (linkPurpose === "identity_change") {
    const completion = readOrdinaryVerificationLinkCompletion(value);
    if (completion?.kind !== "identity_changed") throw invalidCompletion();
    return completion;
  }

  const completion = readOrdinaryVerificationLinkCompletion(value);
  if (completion?.kind === "identity_changed") throw invalidCompletion();
  if (completion) return completion;

  const sessionApplied = applyAuthenticatedSession(value) === true;
  if (sessionApplied) return { kind: "authenticated" };
  return null;
}

export function readOwnedClearedVerificationLinkCompletion(
  completion,
  ownerUserId,
  ownerPrimaryIdentityType,
) {
  if (
    completion?.kind !== "cleared" ||
    publicUserId(completion.user) !== String(ownerUserId || "").trim() ||
    completion.user.verificationIdentityType !== ownerPrimaryIdentityType
  ) {
    throw invalidCompletion();
  }
  return completion.user;
}
